import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import api from '../../services/api';
import MapaLeaflet from '../mapa/MapaLeaflet';
import TiemposArmarDia from './TiemposArmarDia';

const INPUT = 'h-10 px-3 rounded-xl bg-chip text-ink text-body font-bold outline-none w-full';
const caja = 'p-3.5 rounded-2xl bg-card border border-black/[0.07] dark:border-white/[0.07] space-y-2.5';

function Campo({ label, children, ayuda }) {
    return (
        <label className="block">
            <span className="block text-label font-black uppercase tracking-widest text-muted mb-1">{label}</span>
            {children}
            {ayuda && <span className="block text-label text-muted mt-0.5">{ayuda}</span>}
        </label>
    );
}

// Los números con los que se calcula todo: base, pasajes, tiempos y jornada.
export default function TabAjustes({ config, onGuardado }) {
    const [c, setC] = useState(() => JSON.parse(JSON.stringify(config)));
    const [guardando, setGuardando] = useState(false);
    const set = (k, v) => setC(p => ({ ...p, [k]: v }));
    const setPasaje = (i, k, v) => setC(p => ({ ...p, pasajes: p.pasajes.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));

    const guardar = async () => {
        setGuardando(true);
        try {
            const r = await api.put('/rentabilidad/config', {
                ...c, pasajes: c.pasajes.map(t => ({ hastaKm: t.hastaKm === '' || t.hastaKm == null ? null : Number(t.hastaKm), precio: Number(t.precio) })),
            });
            toast.success('Ajustes guardados');
            onGuardado(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo guardar');
        } finally { setGuardando(false); }
    };

    return (
        <div className="space-y-3">
            <div className={caja}>
                <p className="text-body font-black text-ink">Desde dónde salís</p>
                <input value={c.base.nombre} onChange={e => set('base', { ...c.base, nombre: e.target.value })} className={INPUT} />
                <MapaLeaflet puntos={[{ id: 'base', lat: c.base.lat, lng: c.base.lng, color: '#111827', etiqueta: 'B' }]}
                    onClickMapa={ll => set('base', { ...c.base, lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) })}
                    ajustarKey="base" className="h-48 rounded-xl overflow-hidden" />
                <p className="text-label text-muted">Tocá el mapa para mover el punto de salida.</p>
            </div>

            <div className={caja}>
                <p className="text-body font-black text-ink">Depósito</p>
                <Campo label="Nombre"><input value={c.deposito?.nombre || ''} onChange={e => set('deposito', { ...(c.deposito || {}), nombre: e.target.value })} className={INPUT} /></Campo>
                <Campo label="Dirección" ayuda='Para "Volver al depósito" en el Mapa y al terminar el recorrido del día.'>
                    <input value={c.deposito?.direccion || ''} onChange={e => set('deposito', { ...(c.deposito || {}), direccion: e.target.value })}
                        placeholder="Nicaragua 5902, CABA" className={INPUT} />
                </Campo>
            </div>

            <div className={caja}>
                <p className="text-body font-black text-ink">Pasaje de colectivo según la distancia</p>
                {c.pasajes.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <span className="text-caption text-secondary shrink-0 w-12">{t.hastaKm === null || t.hastaKm === '' ? 'Más de' : 'Hasta'}</span>
                        <input value={t.hastaKm ?? ''} onChange={e => setPasaje(i, 'hastaKm', e.target.value.replace(/[^\d.]/g, ''))}
                            placeholder={i > 0 ? String(c.pasajes[i - 1].hastaKm ?? '') : '0'} inputMode="decimal" className={`${INPUT} w-20`} />
                        <span className="text-caption text-secondary shrink-0">km → $</span>
                        <input value={t.precio} onChange={e => setPasaje(i, 'precio', e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={INPUT} />
                        {c.pasajes.length > 1 && (
                            <button type="button" onClick={() => set('pasajes', c.pasajes.filter((_, j) => j !== i))} aria-label="Quitar"
                                className="w-10 h-10 shrink-0 rounded-xl bg-chip text-muted flex items-center justify-center"><LuTrash2 size={14} /></button>
                        )}
                    </div>
                ))}
                <button type="button" onClick={() => set('pasajes', [...c.pasajes, { hastaKm: '', precio: '' }])}
                    className="h-9 px-3 rounded-xl bg-chip text-label font-bold text-secondary inline-flex items-center gap-1"><LuPlus size={14} /> Otro tramo</button>
                <p className="text-label text-muted">Valores aproximados: ajustalos con lo que te cobra la SUBE. El último sin km vale para todo lo que sea más largo.</p>
                <div className="grid grid-cols-2 gap-2">
                    <Campo label="Camino si es menos de (km)"><input value={c.caminarHastaKm} onChange={e => set('caminarHastaKm', e.target.value)} inputMode="decimal" className={INPUT} /></Campo>
                    <Campo label="Factor de calle" ayuda="Línea recta × esto"><input value={c.factorCalle} onChange={e => set('factorCalle', e.target.value)} inputMode="decimal" className={INPUT} /></Campo>
                </div>
            </div>

            <div className={caja}>
                <p className="text-body font-black text-ink">Tiempos</p>
                <div className="grid grid-cols-2 gap-2">
                    <Campo label="Min. por equipo" ayuda="Para Rentabilidad"><input value={c.minutosPorEquipo} onChange={e => set('minutosPorEquipo', e.target.value)} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Colectivo (km/h)" ayuda="Puerta a puerta"><input value={c.velocidadKmH} onChange={e => set('velocidadKmH', e.target.value)} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Espera por viaje (min)"><input value={c.esperaMin} onChange={e => set('esperaMin', e.target.value)} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Objetivo por día ($)" ayuda="Opcional"><input value={c.objetivoDiario ?? ''} onChange={e => set('objetivoDiario', e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Plazo normal (días háb.)" ayuda="Lo acordado con el cliente"><input value={c.plazoNormalDias} onChange={e => set('plazoNormalDias', e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Plazo urgente (días háb.)"><input value={c.plazoUrgenteDias} onChange={e => set('plazoUrgenteDias', e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Jornada desde"><input type="time" value={c.jornadaDesde} onChange={e => set('jornadaDesde', e.target.value)} className={INPUT} /></Campo>
                    <Campo label="Hasta"><input type="time" value={c.jornadaHasta} onChange={e => set('jornadaHasta', e.target.value)} className={INPUT} /></Campo>
                </div>
            </div>

            <TiemposArmarDia c={c} set={set} />

            <div className={caja}>
                <p className="text-body font-black text-ink">Camioneta</p>
                <p className="text-label text-muted">Para cuando vas con la camioneta de la empresa. Sale de su depósito si lo cargaste en Funciones del portal.</p>
                <div className="grid grid-cols-2 gap-2">
                    <Campo label="Velocidad (km/h)" ayuda="Puerta a puerta"><input value={c.camionetaKmH} onChange={e => set('camionetaKmH', e.target.value)} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Estacionar (min)" ayuda="Para Rentabilidad; Armar día usa el de cada zona"><input value={c.camionetaEsperaMin} onChange={e => set('camionetaEsperaMin', e.target.value)} inputMode="numeric" className={INPUT} /></Campo>
                    <Campo label="Nafta ($ por km)" ayuda="0 si la paga la empresa"><input value={c.camionetaCostoKm} onChange={e => set('camionetaCostoKm', e.target.value)} inputMode="decimal" className={INPUT} /></Campo>
                </div>
            </div>

            <button type="button" onClick={guardar} disabled={guardando}
                className="w-full h-12 rounded-2xl bg-brand-red text-white text-label font-black uppercase active:scale-95 disabled:opacity-50">
                {guardando ? 'Guardando…' : 'Guardar ajustes'}
            </button>
        </div>
    );
}
